import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import update from 'immutability-helper';
import InputMask from 'react-input-mask';

import BaseComponent from 'common/BaseComponent';
import {ValidationResult} from 'validation'
import Table from 'common/Table';
import Input from 'common/Input';
import Select from 'common/Select';
import conf from 'configuration.json';
import checkOkIcon from './check_ok.png';
import checkFailIcon from './check_fail.png';
import deleteIcon from './delete.png';
import reloadIcon from './reload.png';
import theme from './InvoiceTab.scss';
import {getMessage as msg} from 'MessageBundle';
import {replacePlaceholders} from 'common/Replace';
import Tooltip from 'common/Tooltip';
import DuplicateTable from './DuplicateTable';
import {stringToFloat, floatToString} from 'utils/Number';

const DUPLICATE_DECISION = ['DUPLICATE', 'Dublette'];

export default class InvoiceTab extends BaseComponent {

    constructor(props) {
        super(props);

        this.state = {
            activeField: "",
            duplicateValue: [],
            duplicateTableShown: false,
            decisions: new Map([
                ['OPEN', 'Offen'],
                ['APPROVED', 'Anerkennung'],
                ['PARTLY_APPROVED', 'Teilanerkennung'],
                ['REJECTED', 'Ablehnung'],
                ['WITHDRAWN', 'Zurückgezogen'],
            ])
        };

        if (props.value.duplicate) {
            this.state.decisions.set(DUPLICATE_DECISION[0], DUPLICATE_DECISION[1]);
        }

        this.taxRates = new Map([
           ['19', '19'],
           ['16', '16'],
           ['0', '0']
        ]);

        this.approved = new Map([
            ['APPROVED', 'Ja'],
            ['NOT_APPROVED', 'Nein']
        ]);

        this.tableStructure = [
            {
                attribute: "status", label: msg("invoiceTab.columnCheck"), headStyle: theme.columnCheck, bodyStyle: theme.txtCenter,
                render: (row) => {
                    const hint = row.retryRequired ? msg("invoiceTab.itemNotValid") : msg("invoiceTab.itemValid");
                    return <img src={row.retryRequired ? checkFailIcon : checkOkIcon} title={hint} alt={hint}/>;
                }
            },
            {
                attribute: "invoiceNumber", label: msg("invoiceTab.columnInvoiceNumber"), headStyle: theme.columnInvoiceNumber, bodyStyle: classnames(theme.columnInvoiceNumber, theme.txtRight),
                render: (row, index) => {
                    if (row.editable) {
                        return <Input id="invoiceNumber-input" disabled={this.props.editableAreas.indexOf('ALL') === -1}
                            value={row.invoiceNumber || ""} onChange={this.handleInvoiceChange("invoiceNumber", index)}
                            allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                    }
                    else {
                        return <div>{row.invoiceNumber || ""}</div>;
                    }
                }
            },
            {
                attribute: "creditNoteNumber", label: msg("invoiceTab.columnCreditNoteNumber"), headStyle: theme.columnCreditNoteNumber, bodyStyle: classnames(theme.columnCreditNoteNumber, theme.txtRight),
                render: (row, index) => {
                    if (row.editable) {
                        return <Input id="creditNoteNumber-input" disabled={this.props.editableAreas.indexOf('ALL') === -1}
                            value={row.creditNoteNumber || ""} onChange={this.handleInvoiceChange("creditNoteNumber", index)}
                            allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                    }
                    else {
                        return <div>{row.creditNoteNumber || ""}</div>;
                    }
                }
            },
            {
                attribute: "invoiceDate", label: msg("invoiceTab.columnInvoiceDate"), fullLabel: msg("invoiceTab.columnInvoiceDate.full"),
                headStyle: theme.columnInvoiceDate, bodyStyle: classnames(theme.columnInvoiceDate, theme.txtCenter),
                render: (row, index) => {
                    return <InputMask disabled={this.props.editableAreas.indexOf('ALL') === -1}
                        value={row.invoiceDate ? row.invoiceDate : ""} onChange={this.handleInvoiceDateChange("invoiceDate", index)}
                        mask="99.99.99" maskChar=" "/>;
                }
            },
            {
                attribute: "deliveryNoteNumber", label: msg("invoiceTab.columnDeliveryNoteNumber"), fullLabel: msg("invoiceTab.columnDeliveryNoteNumber.full"),
                headStyle: theme.columnDeliveryNoteNumber, bodyStyle: classnames(theme.columnDeliveryNoteNumber, theme.txtRight),
                render: (row, index) => {
                    if (row.editable) {
                        return <Input id="deliveryNoteNumber-input" disabled={this.props.editableAreas.indexOf('ALL') === -1}
                            value={row.deliveryNoteNumber || ""} onChange={this.handleInvoiceChange("deliveryNoteNumber", index)}
                            allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                    }
                    else {
                        return <div>{row.deliveryNoteNumber || ""}</div>;
                    }
                }
            },
            {
                attribute: "creditNoteRequestNumber", label: msg("invoiceTab.columnCreditNoteRequestNumber"), fullLabel: msg("invoiceTab.columnCreditNoteRequestNumber.full"),
                headStyle: theme.columnCreditNoteRequestNumber, bodyStyle: classnames(theme.columnCreditNoteRequestNumber, theme.txtRight),
                render: (row, index) => {
                    return <Input id="creditNoteRequestNumber-input" disabled={this.props.editableAreas.indexOf('ALL') === -1} value={row.creditNoteRequestNumber ? row.creditNoteRequestNumber : ""} onChange={this.handleInvoiceChange("creditNoteRequestNumber", index)}
                        allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                }
            },
            {
                attribute: "creditNoteNumberCancellation", label: msg("invoiceTab.columnCreditNoteNumberCancellation"), fullLabel: msg("invoiceTab.columnCreditNoteNumberCancellation.full"),
                headStyle: theme.columnCreditNoteNumberCancellation, bodyStyle: classnames(theme.columnCreditNoteNumberCancellation, theme.txtCenter),
                render: (row, index) => {
                    return <Input id="creditNoteNumberCancellation-input" value={row.creditNoteNumberCancellation ? row.creditNoteNumberCancellation : ""}
                        disabled={this.props.editableAreas.indexOf('ALL') === -1 && this.props.editableAreas.indexOf('CREDIT_NOTE_NUMBER_CANCELATION') === -1}
                        onChange={this.handleInvoiceChange("creditNoteNumberCancellation", index)}
                        allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                }
            },
            {
                attribute: "approvedAmountNet", label: msg("invoiceTab.columnApprovedAmountNet"), fullLabel: msg("invoiceTab.columnApprovedAmountNet.full"),
                headStyle: theme.columnApprovedAmountNet, bodyStyle: classnames(theme.columnApprovedAmountNet, theme.txtRight),
                render: (row, index) => {
                    return <Input id="approvedAmountNet-input" disabled={this.props.editableAreas.indexOf('ALL') === -1} value={row.approvedAmountNet ? row.approvedAmountNet : ""}
                        onChange={this.handleInvoiceAmountsChange("approvedAmountNet", index)} allowedSymbols={"^[0-9.,]$"}/>;
                }
            },
            {
                attribute: "taxRate", label: msg("invoiceTab.columnTaxRatePct"), fullLabel: msg("invoiceTab.columnTaxRatePct.full"),
                headStyle: theme.columnTaxRatePct, bodyStyle: classnames(theme.columnTaxRatePct, theme.txtCenter),
                render: (row, index, errorMessages) => {
                    return (
                        <Select id={"taxRate-select"+index} value={row.taxRate} options={this.taxRates} name="taxRate" index={index} errorMessages={errorMessages}
                            onChange={this.handleInvoiceAmountsChange('taxRate', index)} emptyValue={""}
                            disabled={this.props.editableAreas.indexOf('ALL') === -1} showExclamationMark={false}/>
                    );
                }
            },
            {
                attribute: "approved", label: msg("invoiceTab.columnApproved"), fullLabel: msg("invoiceTab.columnApproved.full"),
                headStyle: theme.columnApproved, bodyStyle: classnames(theme.columnApproved, theme.txtCenter),
                render: (row, index, errorMessages) => {
                    return (
                        <Select id={"approved-select"+index} value={row.approved} options={this.approved} name="approved" index={index} errorMessages={errorMessages}
                            onChange={this.handleInvoiceAmountsChange('approved', index)}
                            disabled={this.props.editableAreas.indexOf('ALL') === -1} emptyValue={""} showExclamationMark={false}/>
                    );
                }
            },
            {
                attribute: "invoiceNumberNew", label: msg("invoiceTab.columnInvoiceNumberNew"), fullLabel: msg("invoiceTab.columnInvoiceNumberNew.full"),
                headStyle: theme.columnInvoiceNumberNew, bodyStyle: classnames(theme.columnInvoiceNumberNew, theme.txtCenter),
                render: (row, index) => {
                    return <Input id="invoiceNumberNew-input" disabled={this.props.editableAreas.indexOf('ALL') === -1}
                        value={row.invoiceNumberNew || ""} onChange={this.handleInvoiceChange("invoiceNumberNew", index)}
                        allowedSymbols={"^[0-9]$"} maxLength={10}/>;
                }
            },
            {
                attribute: "action", label: msg("invoiceTab.columnAction"), headStyle: theme.columnAction, bodyStyle: classnames(theme.columnAction, theme.txtCenter),
                render: (row, id) => {
                    return <a onClick={this.props.editableAreas.indexOf('ALL') === -1 ? () => {} : this.remove(id)} href="javascript:void(0)"
                        title={msg("invoiceTab.deleteButton")}>
                        <img src={deleteIcon} alt=""/>
                    </a>;
                }
            }
        ];

        this.lastRowStructure = [
            {
                attribute: "status", label: msg("invoiceTab.columnCheck"), headStyle: theme.columnCheck, bodyStyle: theme.txtCenter,
                render: () => {
                    return (
                        <button disabled={this.props.editableAreas.indexOf('ALL') === -1} className={theme.buttonGray}
                            onClick={() => {this.setState({newInvoiceShown: true, newInvoice: {}}, () => this.invoiceNumber.focus())}}>
                            {msg("invoiceTab.newButton")}
                        </button>
                    );
                }
            },
            {
                attribute: "invoiceNumber", label: msg("invoiceTab.columnInvoiceNumber"), headStyle: theme.columnInvoiceNumber, bodyStyle: classnames(theme.columnInvoiceNumber, theme.txtRight),
                render: () => {
                    return (
                        this.state.newInvoiceShown ? <Input id="addedInvoiceNumber-input" value={this.state.newInvoice.invoiceNumber ?
                            this.state.newInvoice.invoiceNumber : ""} onChange={this.handleOrderNumberChange("invoiceNumber")}
                            disabled={!(this.state.activeField === "invoiceNumber" || this.state.activeField === '')}
                            allowedSymbols={"^[0-9]$"} maxLength={10}
                            ref={(invoiceNumber) => this.invoiceNumber = invoiceNumber}/>
                            : null
                    );
                }
            },
            {
                attribute: "creditNoteNumber", label: msg("invoiceTab.columnCreditNoteNumber"), headStyle: theme.columnCreditNoteNumber, bodyStyle: classnames(theme.columnCreditNoteNumber, theme.txtRight),
                render: () => {
                    return this.state.newInvoiceShown ? <Input id="addedCreditNoteNumber-input" value={this.state.newInvoice.creditNoteNumber ?
                            this.state.newInvoice.creditNoteNumber : ""} onChange={this.handleOrderNumberChange("creditNoteNumber")}
                            disabled={!(this.state.activeField === "creditNoteNumber" || this.state.activeField === '')}
                            allowedSymbols={"^[0-9]$"} maxLength={10}/> : null;
                }
            },
            {
                attribute: "invoiceDate", label: msg("invoiceTab.columnInvoiceDate"), headStyle: theme.columnInvoiceDate, bodyStyle: classnames(theme.columnInvoiceDate, theme.txtCenter, theme.columnDisable),
                render: () => {
                    return null;
                }
            },
            {
                attribute: "deliveryNoteNumber", label: msg("invoiceTab.columnDeliveryNoteNumber"), headStyle: theme.columnDeliveryNoteNumber, bodyStyle: classnames(theme.columnDeliveryNoteNumber, theme.txtRight),
                render: () => {
                    return this.state.newInvoiceShown ? <Input id="addedColumnDeliveryNoteNumber-input" value={this.state.newInvoice.deliveryNoteNumber ?
                            this.state.newInvoice.deliveryNoteNumber : ""} onChange={this.handleOrderNumberChange("deliveryNoteNumber")}
                            disabled={!(this.state.activeField === "deliveryNoteNumber" || this.state.activeField === '')}
                            allowedSymbols={"^[0-9]$"} maxLength={10}/> : null;
                }
            },
            {
                attribute: "creditNoteRequestNumber", label: msg("invoiceTab.columnCreditNoteRequestNumber"), headStyle: theme.columnCreditNoteRequestNumber, bodyStyle: classnames(theme.columnCreditNoteRequestNumber, theme.txtRight, theme.columnDisable),
                render: () => {
                    return null;
                }
            },
            {
                attribute: "creditNoteNumberCancellation", label: msg("invoiceTab.columnCreditNoteNumberCancellation"), headStyle: theme.columnCreditNoteNumberCancellation, bodyStyle: classnames(theme.columnCreditNoteNumberCancellation, theme.columnDisable),
                render: () => {
                    return null;
                }
            },
            {
                attribute: "approvedAmountNet", label: msg("invoiceTab.columnApprovedAmountNet"), headStyle: theme.columnApprovedAmountNet, bodyStyle: classnames(theme.columnApprovedAmountNet, theme.columnDisable),
                render: () => {
                    return null;
                }
            },
            {
                attribute: "taxRate", label: msg("invoiceTab.columnTaxRatePct"), headStyle: theme.columnTaxRatePct, bodyStyle: classnames(theme.columnTaxRatePct, theme.txtCenter),
                render: () => {
                    return <select disabled />;
                }
            },
            {
                attribute: "approved", label: msg("invoiceTab.columnApproved"), headStyle: theme.columnApproved, bodyStyle: classnames(theme.columnApproved, theme.txtCenter),
                render: () => {
                    return <select disabled />;
                }
            },
            {
                attribute: "invoiceNumberNew", label: msg("invoiceTab.columnInvoiceNumberNew"),  headStyle: theme.columnInvoiceNumberNew, bodyStyle: classnames(theme.columnInvoiceNumberNew, theme.txtCenter, theme.columnDisable),
                render: () => {
                    return null;
                }
            },
            {
                attribute: "action", label: msg("invoiceTab.columnAction"), headStyle: theme.columnAction, bodyStyle: classnames(theme.columnAction, theme.txtCenter),
                render: () => {
                    return (
                        <a href="javascript:void(0)" onClick={this.reload} title={msg("invoiceTab.refreshButton")}>
                            <img src={reloadIcon} alt=""/>
                        </a>
                    );
    }
            }
        ];
    }
    
    componentDidMount() {
        this.loadTaxAmount();
    };

    handleOrderNumberChange = (fieldName) => (e) => {
        this.handleNestedChange("newInvoice", fieldName)(e);
        this.setState({
            activeField: e.target.value === "" ? "" : fieldName
        })
    };

    resetState = () => {
        this.setState({
            newInvoice: {},
            newInvoiceShown: false,
            activeField: ""
        });
    };

    reload = () => {
        const newInvoice = Object.assign({}, this.state.newInvoice);

        this.setState({
            newInvoice: {}
        });

        const accountNumberIsEmpty = newInvoice ? [newInvoice.invoiceNumber, newInvoice.creditNoteNumber, newInvoice.deliveryNoteNumber]
                .every(e => !e) : true;

        if(!accountNumberIsEmpty){
            let updated;
            const validationResult = new ValidationResult(this.props.errorMessages);
            validationResult.resetGroups(["invoiceTab"]);

            let updateInvoices = (invoices) => {
                this.props.onChange({
                    'invoices': invoices,
                    'duplicateCheckPassed': false
                });
            };

            this.request('get', conf.rest.invoiceEndpoint).query(newInvoice).send((result) => {
                this.resetState();
                updated = update(this.props.value.invoices, {$push: [result.body]});
                updateInvoices(updated);
                if ([result.body.invoiceNumber, result.body.deliveryNoteNumber, result.body.creditNoteNumber].filter(item => item === undefined).length > 1) {
                    let accountNumber = [result.body.invoiceNumber, result.body.deliveryNoteNumber, result.body.creditNoteNumber].find(item => item !== undefined);
                    validationResult.setError("invoiceTab", replacePlaceholders(msg("invoiceTab.itemNotFound"), accountNumber));
                }
                this.props.onError(validationResult.getErrorMessages());
            }, () => {
                newInvoice.editable = true;
                newInvoice.retryRequired = true;
                updated = update(this.props.value.invoices, {$push: [newInvoice]});
                updateInvoices(updated);
                this.resetState();
                validationResult.setError("invoiceTab", msg("invoiceTab.sapError"));
                this.props.onError(validationResult.getErrorMessages());
            });
        }
    };

    remove = (rowId) => () => {
        if(confirm(msg("invoiceTab.deleteConfirm"))) {
            const updated = update(this.props.value.invoices, {$splice: [[rowId, 1]]});
            this.props.onChange({
                'invoices': updated,
                'duplicateCheckPassed': false
            });

            const validationResult = new ValidationResult(this.props.errorMessages);
            validationResult.resetByIndex("invoiceTabOnSubmit", rowId);
            this.props.onError(validationResult.getErrorMessages());
        }
    };

    handleInvoiceChange = (name, index) => (event) => {
        let newDuplicateCheckPassed;
        if (['invoiceNumber', 'creditNoteNumber' ,'deliveryNoteNumber'].includes(name)) {
            newDuplicateCheckPassed = false;
        } else {
            newDuplicateCheckPassed = this.props.value.duplicateCheckPassed;
        }
        let updatedInvoices = update(this.props.value.invoices, {$splice: [[index, 1, this.getChangedInvoice(name, index) (event)]]});
        this.props.onChange({
            'invoices': updatedInvoices,
            'duplicateCheckPassed': newDuplicateCheckPassed
        });
    };

    handleInvoiceDateChange = (name, index) => (event) => {
        let updatedInvoices = update(this.props.value.invoices, {$splice: [[index, 1, this.getChangedInvoice(name, index) (event)]]});
        this.props.onChange({
            'invoices': updatedInvoices
        });
    };

    handleInvoiceAmountsChange = (name, index) => (event) => {
        let amounts = {amountTotal: 0, amountNet: 0};
        let updatedInvoices = update(this.props.value.invoices, {$splice: [[index, 1, this.getChangedInvoice(name, index) (event)]]});
        updatedInvoices.forEach(invoice => this.countTaxes(invoice, amounts));

        this.props.onChange({
            'invoices': updatedInvoices,
            'approvedAmountTotal': floatToString(amounts.amountTotal),
            'approvedAmountNet': floatToString(amounts.amountNet)
        });

    };

    getChangedInvoice = (name, index) => (event) => {
        const invoiceChanged = Object.assign({}, this.props.value.invoices[index]);
        invoiceChanged[name] = event.target.value;
        const sapDataField = ['invoiceNumber', 'creditNoteNumber' ,'deliveryNoteNumber', 'invoiceDate'].includes(name);
        const retryRequired = this.props.value.invoices[index].retryRequired;

        //Handling the case when user enters SAP data fields by himself, so in that case we don't use SAP to avoid download invoice data.
        if (sapDataField && retryRequired) {
            invoiceChanged["retryRequired"] = false;
        }
        return invoiceChanged;
    };

    loadTaxAmount = () => {
        let amounts = {amountTotal: 0, amountNet: 0};
        this.props.value.invoices.forEach((value) => {
                this.countTaxes(value, amounts);
            }
        );
        this.props.onChange({
            'approvedAmountTotal': floatToString(amounts.amountTotal),
            'approvedAmountNet': floatToString(amounts.amountNet)
        });
    };

    countTaxes = (invoice, amounts) => {
        if (invoice.approvedAmountNet && invoice.approved === 'APPROVED' && invoice.taxRate) {
            let approvedAmountNet = stringToFloat(invoice.approvedAmountNet);
            amounts.amountNet += approvedAmountNet;
            amounts.amountTotal += approvedAmountNet * (1 + stringToFloat(invoice.taxRate) / 100);
        }
    };

    handleDecisionChange = (e) => {
        const newValue = e.target.value;
        let decisions = new Map(this.state.decisions);
        decisions.delete(DUPLICATE_DECISION[0]);
        this.setState({
            decisions: decisions
        }, () => {
            this.props.onChange({
                'decision': newValue,
                'duplicate': undefined
            });
        });
    };

    handleDuplicateCheck = () => {
        this.setState({
            duplicateValue: [],
        });
        const validationResult = new ValidationResult(this.props.errorMessages);
        validationResult.resetGroups(["invoiceTab"]);
        if (this.props.value.invoices && this.props.value.invoices.length > 0) {
            this.props.onDuplicateSearchStart();
            this.request('post', conf.rest.complaintSearchDuplicateEndpoint).params(this.fillDuplicateSearchRequest()).send((resp) => {
                this.setState({
                    duplicateValue: resp.body,
                    duplicateTableShown: true
                });
                this.props.onDuplicateSearchStop();
                this.props.onError(validationResult.getErrorMessages());
            }, () => {
                validationResult.setError("invoiceTab", msg('common.technicalError'));
                this.props.onError(validationResult.getErrorMessages());
                this.props.onDuplicateSearchStop();
            });
        } else {
            this.setState({
                duplicateTableShown: true
            });
            this.props.onError(validationResult.getErrorMessages());
        }
        this.props.onChange({
            'duplicateCheckPassed': true
        });
    };

    fillDuplicateSearchRequest = () => {
        const invoiceNumbers = [];
        const deliveryNoteNumbers = [];
        this.props.value.invoices.forEach(i => {
            if (i.invoiceNumber && i.invoiceNumber.length > 0) {
                invoiceNumbers.push(i.invoiceNumber);
            }
            if (i.deliveryNoteNumber && i.deliveryNoteNumber.length > 0) {
                deliveryNoteNumbers.push(i.deliveryNoteNumber);
            }
        });
        const request = {
            complaintNumber: this.props.complaintNumber,
        };
        if (invoiceNumbers.length > 0) {
            request.invoiceNumber = invoiceNumbers;
        }
        if (deliveryNoteNumbers.length > 0) {
            request.deliveryNoteNumber = deliveryNoteNumbers;
        }
        return request;
    };

    handleDuplicateChoose = (complaintNumber) => () => {
        let decisions = new Map(this.state.decisions);
        decisions.set(DUPLICATE_DECISION[0], DUPLICATE_DECISION[1]);
        this.setState({
            decisions: decisions
        }, () => {
            this.props.onChange({
                'decision': DUPLICATE_DECISION[0],
                'duplicate': complaintNumber
            });
        });
    };

    render() {
        return (
            <div className={theme.invoiceTab}>
                <div className={theme.tblWrap}>
                    <div className={theme.tblWrapInner}>
                        <Table structure={this.tableStructure} lastRowStructure={this.props.editableAreas.indexOf('ALL') === -1 ? undefined : this.lastRowStructure}
                            value={this.props.value.invoices} errorMessages={this.props.errorMessages} className={theme.invoiceTabTbl}/>
                    </div>
                </div>
                <div className={theme.clear + ' ' + theme.rowDC}>
                    <div className={theme.coll}>
                        <button disabled={this.props.editableAreas.indexOf('ALL') === -1} onClick={this.handleDuplicateCheck}
                            className={this.props.value.duplicateCheckPassed ? theme.buttonGray : theme.buttonAccent}>{msg("invoiceTab.duplicateCheckButton")}</button>
                        <Tooltip value={msg('invoiceTab.duplicateCheckTooltip')}/>
                    </div>
                    <div className={theme.colr}>
                        <Input id="approvedAmountNet" label={msg("invoiceTab.approvedAmountNet")}
                            value={this.props.value.approvedAmountNet || "0"} disabled className={theme.row + " " + theme.rowTotal}/>
                        <Select id="decision-select" name="decision" errorMessages={this.props.errorMessages} label={msg("invoiceTab.decisionSelect")}
                            value={this.props.value.decision} disabled={this.props.editableAreas.indexOf('ALL') === -1}
                            options={this.state.decisions} allowEmpty={false}
                            onChange={this.handleDecisionChange} className={theme.row + " " + theme.rowDecision} required showExclamationMark={false}/>
                        <span className={this.props.value.duplicate ? theme.activeField : theme.notActiveField}>{this.props.value.duplicate}</span>
                    </div>
                </div>
                <div>
                    {this.state.duplicateTableShown ? <DuplicateTable value={this.state.duplicateValue} onChoose={this.handleDuplicateChoose}/> : null}
                </div>
            </div>
        );
    }
}

InvoiceTab.propTypes = {
    /**
     * The invoiceTab part of complaint with all data needed for invoice Tab.
     */
    value: PropTypes.object.isRequired,

    /**
     * Array of error messages to be shown on the tab.
     */
    errorMessage: PropTypes.array,

    /**
     * Function, called in case error is happened
     */
    onError: PropTypes.func,

    /**
     * Function, called to save actual state of invoices
     */
    onDelete: PropTypes.func,

    /**
     * Number of the current complaint.
     */
    complaintNumber: PropTypes.string.required,

    /**
     * Array of areas to be enabled for the user.
     */
    editableAreas: PropTypes.array,

    /**
     * JavaScript code to be executed on "Check for Duplicates" button click.
     */
    onDuplicateSearchStart: PropTypes.func.isRequired,

    /**
     * JavaScript code to be executed as a callback on "Check for Duplicates" button click.
     */
    onDuplicateSearchStop: PropTypes.func.isRequired
};


// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/EditComplaint/InvoiceTab/index.js